export interface TrailSegment {
  index: number;
  distance: number;
  length: number;
  seed: number;
}

export interface TrailVariant {
  dirtColor: number;
  shade: number;
  edgeJitter: number;
  rutWander: number;
  hasPatch: boolean;
  hasLeaves: boolean;
  hasStones: boolean;
  patchLane: number;
}

export const TRAIL_DIRT = 0xb07a48;
export const TRAIL_DIRT_LIGHT = 0xc4945c;
export const TRAIL_DIRT_DARK = 0x8a5530;
export const TRAIL_DIRT_SHADOW = 0x5a3820;
export const TRAIL_GRASS = 0x6e542c;

export function createTrailSegments(
  count: number,
  length: number,
  minDistance: number,
): TrailSegment[] {
  const safeCount = Math.max(2, count);
  const safeLength = Math.max(0.01, length);
  return Array.from({ length: safeCount }, (_, index) => ({
    index,
    distance: minDistance + index * safeLength,
    length: safeLength,
    seed: hashSeed(index, 1),
  }));
}

export function trailSpan(segments: TrailSegment[]): number {
  const length = segments[0]?.length ?? 0;
  return segments.length * length;
}

export function advanceTrailSegments(
  segments: TrailSegment[],
  deltaDistance: number,
  minDistance: number,
): TrailSegment[] {
  if (segments.length === 0) {
    return segments;
  }
  const span = trailSpan(segments);
  for (const segment of segments) {
    segment.distance -= deltaDistance;
  }
  const count = segments.length;
  let recycled = 0;
  while (recycled < count) {
    const nearest = segments[0];
    if (!nearest || nearest.distance >= minDistance) {
      break;
    }
    nearest.distance += span;
    while (nearest.distance < minDistance) {
      nearest.distance += span;
    }
    nearest.seed = hashSeed(nearest.index, nearest.seed + 1);
    segments.push(segments.shift()!);
    recycled += 1;
  }
  return segments;
}

export function sortedTrail(segments: TrailSegment[], into?: TrailSegment[]): TrailSegment[] {
  if (!into) {
    return segments;
  }
  const count = segments.length;
  for (let i = 0; i < count; i += 1) {
    into[i] = segments[i]!;
  }
  into.length = count;
  return into;
}

export function trailJoinsWithoutGaps(segments: TrailSegment[], epsilon = 1e-6): boolean {
  if (segments.length < 2) {
    return true;
  }
  const ordered = sortedTrail(segments);
  const length = ordered[0]?.length ?? 0;
  for (let i = 0; i < ordered.length - 1; i += 1) {
    const current = ordered[i];
    const next = ordered[i + 1];
    if (!current || !next) {
      return false;
    }
    if (Math.abs(next.distance - (current.distance + current.length)) > epsilon) {
      return false;
    }
  }
  return Math.abs((ordered[0]?.length ?? 0) - length) <= epsilon;
}

export function variantFromSeed(seed: number): TrailVariant {
  const shade = (seed % 17) / 17;
  return {
    dirtColor: shade > 0.62 ? TRAIL_DIRT_DARK : shade < 0.32 ? TRAIL_DIRT_LIGHT : TRAIL_DIRT,
    shade,
    edgeJitter: ((seed % 23) - 11) / 11,
    rutWander: ((seed % 19) - 9) / 9,
    hasPatch: seed % 3 === 0,
    hasLeaves: seed % 2 === 0,
    hasStones: seed % 3 === 1,
    patchLane: ((seed % 11) - 5) / 7,
  };
}

export function hashSeed(index: number, salt: number): number {
  let n = Math.imul(index + 1, 374761393) + Math.imul(salt + 1, 668265263);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return n >>> 0;
}

export const RIBBON_NEAR_BAND = 0.25;
export const RIBBON_TARGET_PIXEL_STEP = 7;
export const RIBBON_OVERSCAN_PX = 24;
export const RIBBON_MAX_SAMPLES = 64;
export const EDGE_JITTER_WIDTH_RATIO = 0.11;
export const MAX_EDGE_JITTER_PX = 6;

export function interpolatedEdgeJitter(segments: TrailSegment[], distance: number): number {
  return interpolatedEdgeJitterOrdered(sortedTrail(segments), distance);
}

export function interpolatedEdgeJitterOrdered(ordered: TrailSegment[], distance: number): number {
  if (ordered.length === 0) {
    return 0;
  }
  if (ordered.length === 1) {
    return variantFromSeed(ordered[0]!.seed).edgeJitter;
  }

  let index = 0;
  while (index < ordered.length - 1 && ordered[index + 1]!.distance <= distance) {
    index += 1;
  }
  const current = ordered[index]!;
  const next = ordered[index + 1];
  const currentJitter = variantFromSeed(current.seed).edgeJitter;
  if (!next) {
    return currentJitter;
  }
  const span = Math.max(1e-6, next.distance - current.distance);
  const t = clamp01((distance - current.distance) / span);
  return currentJitter + (variantFromSeed(next.seed).edgeJitter - currentJitter) * t;
}

export function cappedEdgeJitter(
  edgeJitter: number,
  roadHalfWidth: number,
  maxPx = MAX_EDGE_JITTER_PX,
): number {
  const raw = roadHalfWidth * EDGE_JITTER_WIDTH_RATIO * edgeJitter;
  return Math.max(-maxPx, Math.min(maxPx, raw));
}

export function sampleRibbonDistances(
  segments: TrailSegment[],
  projectY: (distance: number) => number,
  options: {
    nearDistance?: number;
    nearBand?: number;
    targetPixelStep?: number;
    farStep?: number;
    minStep?: number;
    maxSamples?: number;
    ordered?: TrailSegment[];
    into?: number[];
  } = {},
): number[] {
  const ordered = options.ordered ?? sortedTrail(segments);
  const distances = options.into ?? [];
  distances.length = 0;
  if (ordered.length === 0) {
    return distances;
  }

  const nearest = ordered[0]!;
  const farthest = ordered[ordered.length - 1]!;
  const start = farthest.distance + farthest.length;
  const end = options.nearDistance ?? nearest.distance;
  if (end >= start) {
    distances.push(start);
    return distances;
  }

  const nearBand = options.nearBand ?? RIBBON_NEAR_BAND;
  const targetPixelStep = options.targetPixelStep ?? RIBBON_TARGET_PIXEL_STEP;
  const farStep = options.farStep ?? nearest.length;
  const minStep = options.minStep ?? 0.002;
  const maxSamples = options.maxSamples ?? RIBBON_MAX_SAMPLES;

  distances.push(start);
  let distance = start;

  while (distance > end + 1e-9 && distances.length < maxSamples - 1) {
    const tentative = Math.max(end, distance - farStep);
    let step = farStep;
    if (Math.min(distance, tentative) < nearBand) {
      const y = projectY(distance);
      const probe = Math.max(end, distance - Math.max(minStep * 8, farStep * 0.2));
      const probeSpan = distance - probe;
      if (probeSpan > 1e-9) {
        const pxPerWorld = Math.abs(projectY(probe) - y) / probeSpan;
        if (pxPerWorld > 1e-6) {
          step = targetPixelStep / pxPerWorld;
        }
      }
      step = Math.min(farStep, Math.max(minStep, step));
    }
    const next = Math.max(end, distance - step);
    if (next >= distance - 1e-12) {
      break;
    }
    distance = next;
    distances.push(distance);
  }

  const last = distances[distances.length - 1]!;
  if (Math.abs(last - end) > 1e-8) {
    distances.push(end);
  }

  return distances;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
